import {
  advancedFilter,
  Conditions,
  FilterField,
  ProjectBusinessComponent,
  ProjectPurpose,
  RdProjectSortFields,
  RdProjectsSummaryFilter,
} from '@neo.tax/shared';

import { RdProject } from '../hooks/useRdProjects';
import { RdProjectFilterableColumn } from '../types';

export type RdDocumentationStatus = {
  /** This is the only field that should be false. Don't want disallowances. */
  disallowances: boolean;
  purpose: boolean;
  businessComponent: boolean;
  technicalChallenges: boolean;
  narrative: boolean;
  processOfExperimentation: boolean;
};

const rdDocumentationTitles: Record<keyof RdDocumentationStatus, string> = {
  disallowances: 'Disallowances',
  purpose: 'Purpose',
  businessComponent: 'Business Component',
  technicalChallenges: 'Technical Challenges',
  narrative: 'Project Narrative',
  processOfExperimentation: 'Process of Experimentation',
};

export const hasDisallowances = (project: RdProject): boolean =>
  !!project.disallowances && project.disallowances.length > 0;

export const hasPurpose = (project: RdProject): boolean =>
  !!(
    project.purpose === ProjectPurpose.CreateNewProduct ||
    (project.purpose === ProjectPurpose.ImproveExistingProduct &&
      project.improvedAspects &&
      project.improvedAspects.length > 0)
  );

export const hasBusinessComponent = (project: RdProject): boolean => {
  if (!project.businessComponent) {
    return false;
  }

  if (project.businessComponent === ProjectBusinessComponent.ComputerSoftware) {
    return !!project.businessPurpose;
  }

  return true;
};

export const hasTechnicalChallenges = (project: RdProject): boolean => !!project.technicalChallenges;

export const hasNarrative = (project: RdProject): boolean => !!project.description;

export const hasProcessOfExperimentation = (project: RdProject): boolean => !!project.processOfExperimentation;

const completedDisallowances = (project: RdProject): boolean => !!project.disallowances;

export const hasAllDocumentation = (project: RdProject): boolean =>
  !hasDisallowances(project) &&
  hasPurpose(project) &&
  hasBusinessComponent(project) &&
  hasTechnicalChallenges(project) &&
  hasNarrative(project) &&
  hasProcessOfExperimentation(project);

export const getRdDocumentationStatus = (project: RdProject): RdDocumentationStatus => ({
  disallowances: completedDisallowances(project),
  purpose: hasPurpose(project),
  businessComponent: hasBusinessComponent(project),
  technicalChallenges: hasTechnicalChallenges(project),
  narrative: hasNarrative(project),
  processOfExperimentation: hasProcessOfExperimentation(project),
});

export const makeRdDetailsDescription = (rdDocumentationStatus: RdDocumentationStatus): string => {
  let message = '';
  if (!rdDocumentationStatus.disallowances) {
    message += 'Project has disallowed activities. ';
  }

  const entries: [keyof RdDocumentationStatus, boolean][] = Object.entries(rdDocumentationStatus) as [
    keyof RdDocumentationStatus,
    boolean,
  ][];
  const missingDocumentation = entries
    .filter(([key, hasAllDocumentation]) => !hasAllDocumentation && key !== 'disallowances')
    .map(([key]) => rdDocumentationTitles[key]);
  return message + `Project is missing the following supporting documentation: ${missingDocumentation.join(', ')}.`;
};

export const mapStringToSortField = (fieldName: string): RdProjectSortFields => {
  switch (fieldName) {
    case 'name':
      return RdProjectSortFields.Name;
    case 'projectOwnerName':
      return RdProjectSortFields.ProjectOwnerName;
    case 'productName':
      return RdProjectSortFields.ProductName;
    case 'ticketCount':
      return RdProjectSortFields.TicketCount;
    case 'selectedCategory':
      return RdProjectSortFields.SelectedCategory;
    case 'rdDocumentationStatus':
      return RdProjectSortFields.RdDocumentationStatus;
    case 'workstreamNamesSearch':
      return RdProjectSortFields.WorkstreamNamesSearch;
    case 'aiGuidance':
      return RdProjectSortFields.AiGuidance;
    default:
      return RdProjectSortFields.Name;
  }
};

export const mapRdProjectsSummaryFilterConditions = (
  conditions: Conditions<RdProjectFilterableColumn> | undefined,
):
  | { and: Pick<RdProjectsSummaryFilter, FilterField<RdProjectsSummaryFilter>>[] }
  | { or: Pick<RdProjectsSummaryFilter, FilterField<RdProjectsSummaryFilter>>[] } => {
  if (!conditions) {
    return { and: [] };
  }

  return advancedFilter<RdProjectsSummaryFilter>(conditions);
};
